<script setup lang="ts">
import { ref, watch } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';

interface Props {
  isOpen: boolean;
  title: string;
  label: string;
  currentValue: string;
  placeholder?: string;
  maxLength?: number;
  validateFn?: (value: string) => string | null;
  isSaving?: boolean;
  inputType?: 'text' | 'phone';
}

interface Emits {
  (e: 'close'): void;
  (e: 'save', value: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  maxLength: 100,
  inputType: 'text'
});

const emit = defineEmits<Emits>();

const textValue = ref('');
const errorMessage = ref<string | null>(null);

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    textValue.value = props.currentValue;
    errorMessage.value = null;
  }
});

watch(textValue, () => {
  errorMessage.value = null;
});

// Handle input filtering for phone numbers
const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let value = target.value;
  
  if (props.inputType === 'phone') {
    // Remove any non-digit characters
    value = value.replace(/\D/g, '');
    
    // Limit to 11 digits
    if (value.length > 11) {
      value = value.substring(0, 11);
    }
    
    // Update the input value and reactive ref
    target.value = value;
    textValue.value = value;
  }
};

const handleSave = () => {
  const trimmedValue = textValue.value.trim();
  
  if (props.validateFn) {
    const error = props.validateFn(trimmedValue);
    if (error) {
      errorMessage.value = error;
      return;
    }
  }
  
  emit('save', trimmedValue);
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 z-[9999]"
      @click.self="handleClose"
    >
      <div
        class="bg-white rounded-2xl my-4 shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black max-w-md w-full transform transition-all"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 pt-4">
          <h2 class="text-2xl font-bold text-gray-900">{{ title }}</h2>
          <button
            @click="handleClose"
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>

        <!-- Content -->
        <div class="p-6">
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              {{ label }}
            </label>
            <input
              v-model="textValue"
              type="text"
              :placeholder="placeholder"
              :maxlength="inputType === 'phone' ? 11 : maxLength"
              :pattern="inputType === 'phone' ? '[0-9]*' : undefined"
              :inputmode="inputType === 'phone' ? 'numeric' : 'text'"
              @input="handleInput"
              :class="[
                'w-full px-4 py-3 border-2 rounded-lg focus:ring-2 transition-all',
                errorMessage 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
              ]"
            />
          </div>
          <!-- Error Message -->
          <div v-if="errorMessage" class="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <svg class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
            <p class="text-sm text-red-700">{{ errorMessage }}</p>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 p-6">
          <button
            @click="handleClose"
            class="px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            @click="handleSave"
            :disabled="isSaving"
            class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-md flex items-center gap-2"
          >
            <svg v-if="isSaving" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>{{ isSaving ? 'Saving...' : 'Save Changes' }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.9);
}
</style>

